import { useLogger } from "@bindery/logging/react";
import {
  Drawer,
  DrawerClose,
  DrawerContent,
  DrawerHeader,
  DrawerTitle,
  DrawerTrigger,
} from "@bindery/ui/components/ui/drawer";
import { ModeToggle } from "@bindery/ui/components/theme/mode-toggle";
import { cn } from "@bindery/ui/lib/utils";
import { Bars3Icon } from "@heroicons/react/20/solid";
import { useState } from "react";
import { useDesign } from "@/components/design-switcher";
import { BrandLogo } from "./brand";
import { DesignSwitcherCompact } from "./design-switcher-menu";
import { NavLinks } from "./nav-links";
import { UserMenu } from "./user-menu";

/** A bottom drawer: easier for a child's thumb than a dropdown. */
export function MobileMenu({ triggerColor }: { triggerColor?: string }) {
  const [open, setOpen] = useState(false);
  const { activeSlug, setActiveSlug } = useDesign();
  const log = useLogger();

  const handleOpenChange = (next: boolean) => {
    log.event("web.navigation.menu_toggle", { open: next });
    setOpen(next);
  };
  const closeMenu = () => handleOpenChange(false);

  return (
    <Drawer open={open} onOpenChange={handleOpenChange}>
      <DrawerTrigger asChild>
        <button
          type="button"
          className={cn(
            "flex h-9 w-9 items-center justify-center rounded-xl transition-all duration-150 hover:bg-white/[0.06] md:hidden",
            !triggerColor && "text-white/60 hover:text-white/90",
          )}
          style={triggerColor ? { color: triggerColor } : undefined}
          aria-label="Open menu"
        >
          <Bars3Icon className="h-5 w-5" />
        </button>
      </DrawerTrigger>
      <DrawerContent className="border-white/10 bg-black/90 text-white backdrop-blur-2xl">
        <DrawerHeader className="flex items-center justify-center pb-2">
          <DrawerTitle>
            <BrandLogo large onNavigate={closeMenu} />
          </DrawerTitle>
        </DrawerHeader>
        <div className="flex max-h-[calc(100vh-180px)] flex-col gap-6 overflow-y-auto px-6 pb-8">
          <NavLinks orientation="vertical" onNavigate={closeMenu} />
          <div className="h-px bg-white/10" />
          <DesignSwitcherCompact
            activeSlug={activeSlug}
            onSelect={(slug) => {
              setActiveSlug(slug);
              closeMenu();
            }}
          />
          <div className="h-px bg-white/10" />
          <div className="flex items-center justify-between px-3">
            <span className="text-sm text-white/50">Theme</span>
            <ModeToggle className="rounded-xl text-white/80 hover:bg-white/[0.08] hover:text-white" />
          </div>
          <div className="h-px bg-white/10" />
          <div className="pb-20">
            <UserMenu inline onNavigate={closeMenu} />
          </div>
        </div>
        <DrawerClose className="sr-only">Close</DrawerClose>
      </DrawerContent>
    </Drawer>
  );
}
